import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { OmniCampaign } from '@feathr/blackbox';
import {
  Alert,
  CardV2 as Card,
  SegmentedControl,
  SplitSlider,
  Text,
  Tooltip,
} from '@feathr/components';
import { getIconForAction, useDeferredLoading } from '@feathr/hooks';
import type { IUseAudienceBreakdown } from '@feathr/report_components';

import { getDays } from '../AdWizardBudgetStep.utils';
import { useChannelBudgetSplit } from './ChannelBudgetSplit.hooks';
import { CHANNEL_MIN_DAILY_BUDGET, MAX_SPLIT, MIN_SPLIT } from './ChannelBudgetSplit.utils';

import * as styles from './ChannelBudgetSplit.css';

/**
 * Props for the `ChannelBudgetSplit` card that lets users divide an OmniCampaign's
 * total budget between the Display (TTD) and Meta channels.
 */
interface IChannelBudgetSplitProps {
  /** OmniCampaign whose `exposure_settings.channel_split` is being edited. */
  campaign: OmniCampaign;
  /** Audience breakdown used to derive the recommended split and budgets. */
  audienceData: IUseAudienceBreakdown;
}

function ChannelBudgetSplit({
  campaign,
  audienceData,
}: Readonly<IChannelBudgetSplitProps>): JSX.Element {
  const { t } = useTranslation();

  const {
    displayPercent,
    setDisplayPercent,
    displayBudget,
    metaBudget,
    showAudienceDriftWarning,
    handleSliderChangeEnd,
    handleUseRecommended,
    recommendedDisplayPercent,
  } = useChannelBudgetSplit({ campaign, audienceData });

  const metaPercent = 100 - displayPercent;
  const recommendationDiff = Math.abs(displayPercent - recommendedDisplayPercent);
  const isMatchingRecommended = recommendationDiff === 0;
  const recommendationText = isMatchingRecommended
    ? t("You're matching the recommended split")
    : t('Off recommendation by {{diff}}%', { diff: recommendationDiff });

  // Inline preview of the same check `validateStepBudget` uses to block save.
  const days = getDays(campaign);
  const isBelowFloor =
    days > 0 &&
    (displayBudget / days < CHANNEL_MIN_DAILY_BUDGET ||
      metaBudget / days < CHANNEL_MIN_DAILY_BUDGET);

  /* `useAudienceBreakdown` initializes `isLoading=false` and only flips
   * it true inside its first effect, which causes a brief flash of content
   * before the skeleton. `useDeferredLoading` forces the first render to
   * be loading and follows the upstream flag from there. */
  const isLoading = useDeferredLoading(audienceData.isLoading);

  const tooltipContent = t(
    'Adjust how your budget is divided between Display and Meta advertising channels. The default split is based on the relative audience sizes for each channel.',
  );

  const titleSuffix = (
    <Tooltip title={tooltipContent}>
      <FontAwesomeIcon icon={faCircleInfo} />
    </Tooltip>
  );

  const description = t(
    'Drag the divider to adjust the split between Display and Meta. Each channel needs at least {{min}} per day.',
    { min: numeral(CHANNEL_MIN_DAILY_BUDGET).format('$0,0') },
  );

  const modeOptions = [
    { value: 'recommended', label: t('Use recommended budget') },
    { value: 'manual', label: t('Set manually') },
  ];

  function handleModeChange(next: string): void {
    if (next === 'recommended') {
      handleUseRecommended();
    } else {
      /* Switching to manual snaps the split to an even 50/50 as a neutral
       * starting point the user can drag from. */
      setDisplayPercent(50);
      handleSliderChangeEnd(50);
    }
  }

  return (
    <Card width={'narrow'}>
      <Card.Header
        description={description}
        title={t('Channel Budget Split')}
        titleSuffix={titleSuffix}
      />
      <Card.Content isLoading={isLoading}>
        <Flex className={styles.cardSection} direction={'column'}>
          {showAudienceDriftWarning && (
            <Alert
              description={t(
                'Your audience selection has changed. Please review your budget split to ensure it still aligns with your campaign goals.',
              )}
              name={'audience_drift_warning'}
              title={t('Audience has changed')}
              type={'warning'}
            />
          )}

          <SegmentedControl
            data={modeOptions}
            name={'budget_split_mode'}
            onChange={handleModeChange}
            value={isMatchingRecommended ? 'recommended' : 'manual'}
          />

          <Flex className={styles.summaryRow} justify={'space-between'}>
            <Flex className={styles.summaryColumn} direction={'column'}>
              <Text.DefaultSemibold>{t('Display')}</Text.DefaultSemibold>
              <Flex direction={'column'}>
                <Text.SmallRegular>{numeral(displayPercent / 100).format('0%')}</Text.SmallRegular>
                <Text.LargeSemibold className={styles.dollarAmount}>
                  {numeral(displayBudget).format('$0,0.00')}
                </Text.LargeSemibold>
              </Flex>
            </Flex>
            <Flex className={styles.summaryColumnRight} direction={'column'}>
              <Text.DefaultSemibold>{t('Meta')}</Text.DefaultSemibold>
              <Flex align={'flex-end'} direction={'column'}>
                <Text.SmallRegular>{numeral(metaPercent / 100).format('0%')}</Text.SmallRegular>
                <Text.LargeSemibold className={styles.dollarAmount}>
                  {numeral(metaBudget).format('$0,0.00')}
                </Text.LargeSemibold>
              </Flex>
            </Flex>
          </Flex>

          <SplitSlider
            dataName={'channel_split_slider'}
            formatValue={(v): string => numeral(v / 100).format('0%')}
            hideValues={true}
            leftIcon={<span className={styles.displayIcon}>{getIconForAction('displayAd')}</span>}
            /* Drop the label when the region collapses to its minimum so the
             * affordance is just the icon, perfectly centered. */
            leftLabel={displayPercent > MIN_SPLIT ? t('Display') : undefined}
            max={MAX_SPLIT}
            min={MIN_SPLIT}
            minRegionFlex={MIN_SPLIT}
            onChange={setDisplayPercent}
            onChangeEnd={handleSliderChangeEnd}
            rightIcon={<span className={styles.metaIcon}>{getIconForAction('meta')}</span>}
            rightLabel={displayPercent < MAX_SPLIT ? t('Meta') : undefined}
            step={1}
            value={displayPercent}
          />

          {isBelowFloor ? (
            <Alert
              className={styles.flushAlert}
              description={t(
                "Each channel needs at least {{min}} per day. Drag the slider or adjust the campaign's total budget to fix it.",
                { min: numeral(CHANNEL_MIN_DAILY_BUDGET).format('$0,0') },
              )}
              name={'channel_split_below_floor'}
              title={t('This split is below the per-channel minimum')}
              type={'danger'}
            />
          ) : (
            <Text.SmallRegular className={styles.recommendationText}>
              {recommendationText}
            </Text.SmallRegular>
          )}
        </Flex>
      </Card.Content>
    </Card>
  );
}

export default observer(ChannelBudgetSplit);
